import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Linkedin, MapPin } from "lucide-react";
import { Reveal, SectionTag } from "./Reveal";
import BrandMark from "./BrandMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INVESTOR_TYPES = ["Angel Investor", "Private Investor", "Venture Capital", "Family Office", "Other"];

const CONTACT_INFO = [
  { icon: Mail, label: "Email", value: "info@airobotcamstudio.com", href: "mailto:info@airobotcamstudio.com", testid: "contact-info-email" },
  {
    icon: Linkedin,
    label: "LinkedIn",
    value: "AIROBOTCAMSTUDIO",
    href: "https://www.linkedin.com/in/airobotcamstudio",
    testid: "contact-info-linkedin",
  },
  {
    icon: MapPin,
    label: "Registered Office",
    value: "71–75 Shelton Street, Covent Garden, London, United Kingdom, WC2H 9JQ",
    testid: "contact-info-address",
  },
];

const inputCls =
  "w-full rounded-md border border-white/12 bg-black/40 px-4 py-3 text-sm text-white placeholder-white/30 outline-none transition-colors duration-300 focus:border-cyan-300/60";

const Field = ({ label, id, children }) => (
  <div>
    <label htmlFor={id} className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70">
      {label}
    </label>
    {children}
  </div>
);

export default function Contact() {
  const [general, setGeneral] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [investor, setInvestor] = useState({ name: "", email: "", investor_type: "Angel Investor", message: "" });
  const [beta, setBeta] = useState({ name: "", email: "", country: "", message: "" });
  const [sending, setSending] = useState({ general: false, investor: false, beta: false });

  const submit = async (kind, url, data, reset, successMsg) => {
    setSending((s) => ({ ...s, [kind]: true }));
    try {
      await axios.post(`${API}${url}`, data);
      toast.success(successMsg);
      reset();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        typeof detail === "string" && detail
          ? detail
          : "Sorry, your message could not be sent. Please try again or email us directly at info@airobotcamstudio.com."
      );
    } finally {
      setSending((s) => ({ ...s, [kind]: false }));
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative overflow-hidden py-16 lg:py-36">
      <div className="pointer-events-none absolute left-1/2 top-10 h-[380px] w-[680px] -translate-x-1/2 rounded-full bg-cyan-400/[0.05] blur-[140px]" />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <div className="flex items-center gap-4">
            <BrandMark className="h-10 w-10 rounded-lg" testid="contact-logo" />
            <SectionTag>Contact</SectionTag>
          </div>
          <h2 className="font-display mt-5 text-3xl font-light tracking-tighter text-white sm:text-4xl lg:text-5xl">
            Get in <span className="text-cyan-300">Touch</span>
          </h2>
          <p className="mt-4 text-sm font-semibold uppercase tracking-[0.25em] text-white/50">
            AIROBOTCAMSTUDIO LTD
          </p>
        </Reveal>

        <div className="mt-12 grid gap-4 sm:grid-cols-3">
          {CONTACT_INFO.map(({ icon: Icon, label, value, testid, href }, i) => (
            <Reveal key={label} delay={0.06 * i}>
              <div
                data-testid={testid}
                className="h-full rounded-xl border border-white/10 bg-white/[0.03] p-6 transition-colors duration-500 hover:border-cyan-300/30"
              >
                <Icon size={18} className="text-cyan-300/80" />
                <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70">{label}</p>
                {href ? (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 block w-fit text-sm leading-relaxed text-cyan-300/80 underline-offset-4 transition-colors duration-300 hover:text-cyan-100 hover:underline hover:decoration-cyan-300/40"
                  >
                    {value}
                  </a>
                ) : (
                  <p className="mt-2 text-sm leading-relaxed text-white/80">{value}</p>
                )}
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-10 grid gap-10 lg:grid-cols-2 xl:grid-cols-3">
          <Reveal>
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.02] p-8 lg:p-10">
              <SectionTag>Contact Form</SectionTag>
              <h3 className="font-display mt-4 text-2xl font-light tracking-tighter text-white">
                Send Us a <span className="text-cyan-300">Message</span>
              </h3>
              <form
                data-testid="contact-form"
                className="mt-8 space-y-5"
                onSubmit={(e) => {
                  e.preventDefault();
                  submit("general", "/contact", general, () =>
                    setGeneral({ name: "", email: "", subject: "", message: "", website: "" })
                  , "Thank you. Your message has been sent successfully. We'll get back to you as soon as possible.");
                }}
              >
                <input
                  type="text"
                  data-testid="contact-honeypot"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute -left-[9999px] h-0 w-0 opacity-0"
                  value={general.website}
                  onChange={(e) => setGeneral({ ...general, website: e.target.value })}
                />
                <Field label="Full Name" id="ct-name">
                  <input id="ct-name" data-testid="contact-name-input" required maxLength={120} className={inputCls} value={general.name}
                    onChange={(e) => setGeneral({ ...general, name: e.target.value })} placeholder="Your full name" />
                </Field>
                <Field label="Email Address" id="ct-email">
                  <input id="ct-email" data-testid="contact-email-input" required type="email" className={inputCls} value={general.email}
                    onChange={(e) => setGeneral({ ...general, email: e.target.value })} placeholder="you@example.com" />
                </Field>
                <Field label="Subject" id="ct-subject">
                  <input id="ct-subject" data-testid="contact-subject-input" required maxLength={200} className={inputCls} value={general.subject}
                    onChange={(e) => setGeneral({ ...general, subject: e.target.value })} placeholder="What is this about?" />
                </Field>
                <Field label="Message" id="ct-message">
                  <textarea id="ct-message" data-testid="contact-message-input" required rows={5} maxLength={4000} className={inputCls} value={general.message}
                    onChange={(e) => setGeneral({ ...general, message: e.target.value })} placeholder="Your message" />
                </Field>
                <button
                  data-testid="contact-submit-btn"
                  type="submit"
                  disabled={sending.general}
                  className="w-full rounded-md bg-cyan-300 px-6 py-3.5 text-sm font-bold text-black transition-transform duration-300 hover:-translate-y-0.5 hover:bg-cyan-200 disabled:opacity-50"
                >
                  {sending.general ? "Sending…" : "Send Message"}
                </button>
              </form>
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.02] p-8 lg:p-10">
              <SectionTag>For Investors</SectionTag>
              <h3 className="font-display mt-4 text-2xl font-light tracking-tighter text-white">
                Interested in <span className="text-cyan-300">Investing?</span>
              </h3>
              <form
                data-testid="investor-form"
                className="mt-8 space-y-5"
                onSubmit={(e) => {
                  e.preventDefault();
                  submit("investor", "/investor-interest", investor, () =>
                    setInvestor({ name: "", email: "", investor_type: "Angel Investor", message: "" })
                  , "Thank you — your investor interest has been registered.");
                }}
              >
                <Field label="Name" id="inv-name">
                  <input id="inv-name" data-testid="investor-name-input" required className={inputCls} value={investor.name}
                    onChange={(e) => setInvestor({ ...investor, name: e.target.value })} placeholder="Your full name" />
                </Field>
                <Field label="Email" id="inv-email">
                  <input id="inv-email" data-testid="investor-email-input" required type="email" className={inputCls} value={investor.email}
                    onChange={(e) => setInvestor({ ...investor, email: e.target.value })} placeholder="you@example.com" />
                </Field>
                <Field label="Investor Type" id="inv-type">
                  <select id="inv-type" data-testid="investor-type-select" className={inputCls} value={investor.investor_type}
                    onChange={(e) => setInvestor({ ...investor, investor_type: e.target.value })}>
                    {INVESTOR_TYPES.map((t) => (
                      <option key={t} value={t} className="bg-black">{t}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Message" id="inv-message">
                  <textarea id="inv-message" data-testid="investor-message-input" rows={4} className={inputCls} value={investor.message}
                    onChange={(e) => setInvestor({ ...investor, message: e.target.value })} placeholder="Tell us a little about your interest" />
                </Field>
                <button
                  data-testid="investor-submit-btn"
                  type="submit"
                  disabled={sending.investor}
                  className="w-full rounded-md bg-cyan-300 px-6 py-3.5 text-sm font-bold text-black transition-transform duration-300 hover:-translate-y-0.5 hover:bg-cyan-200 disabled:opacity-50"
                >
                  {sending.investor ? "Sending…" : "Register Investor Interest"}
                </button>
                <p className="text-[11px] leading-relaxed text-white/55">
                  This form is for expressions of interest only. No investment can be made through
                  this website.
                </p>
              </form>
            </div>
          </Reveal>

          <Reveal delay={0.16}>
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.02] p-8 lg:p-10">
              <SectionTag>Beta Interest</SectionTag>
              <h3 className="font-display mt-4 text-2xl font-light tracking-tighter text-white">
                Join the Future of <span className="text-cyan-300">AI Interaction</span>
              </h3>
              <form
                data-testid="beta-form"
                className="mt-8 space-y-5"
                onSubmit={(e) => {
                  e.preventDefault();
                  submit("beta", "/beta-interest", beta, () =>
                    setBeta({ name: "", email: "", country: "", message: "" })
                  , "Thank you — you've joined the beta interest list.");
                }}
              >
                <Field label="Name" id="beta-name">
                  <input id="beta-name" data-testid="beta-name-input" required className={inputCls} value={beta.name}
                    onChange={(e) => setBeta({ ...beta, name: e.target.value })} placeholder="Your name" />
                </Field>
                <Field label="Email" id="beta-email">
                  <input id="beta-email" data-testid="beta-email-input" required type="email" className={inputCls} value={beta.email}
                    onChange={(e) => setBeta({ ...beta, email: e.target.value })} placeholder="you@example.com" />
                </Field>
                <Field label="Country" id="beta-country">
                  <input id="beta-country" data-testid="beta-country-input" required className={inputCls} value={beta.country}
                    onChange={(e) => setBeta({ ...beta, country: e.target.value })} placeholder="United Kingdom" />
                </Field>
                <Field label="Message (optional)" id="beta-message">
                  <textarea id="beta-message" data-testid="beta-message-input" rows={4} className={inputCls} value={beta.message}
                    onChange={(e) => setBeta({ ...beta, message: e.target.value })} placeholder="Anything you'd like us to know" />
                </Field>
                <button
                  data-testid="beta-submit-btn"
                  type="submit"
                  disabled={sending.beta}
                  className="w-full rounded-md border border-cyan-300/30 px-6 py-3.5 text-sm font-semibold text-cyan-200/90 transition-all duration-300 hover:-translate-y-0.5 hover:border-cyan-300/60 hover:text-cyan-100 disabled:opacity-50"
                >
                  {sending.beta ? "Sending…" : "Join Beta Interest List"}
                </button>
                <p className="text-[11px] leading-relaxed text-white/55">
                  The MVP is currently in development. Register your interest to receive future
                  updates about testing opportunities.
                </p>
              </form>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
